import { IManagedUserRecentSummary } from '@/types';
import { TManagerDashboardFilter } from '../types';

/**
 * Helper function to filter the list of managed users to those who are
 * certified for the active certification.
 */
export const filterCertifiedUsers = (
  users: IManagedUserRecentSummary[] = [],
): IManagedUserRecentSummary[] => {
  return users.filter((user) => {
    // If the user does not have a most recent certificate, they cannot be certified
    if (!user.mostRecentCertificate) return false;
    if (!user.mostRecentCertificate === null) return false;

    // A user is considered certified if their most recent certificate has a passed status of true
    const activeCert = user?.mostRecentCertificate?.passed === true;
    return activeCert;
  });
};

/**
 * Helper function to filter the list of managed users to those who
 * have passed the practice.
 */
export const filterPracticePassedUsers = (
  users: IManagedUserRecentSummary[] = [],
): IManagedUserRecentSummary[] => {
  return users.filter((user) => {
    // If the user does not have a most recent practice,
    // they cannot be considered to have passed the practice
    if (!user.mostRecentPractice) return false;
    if (!user.mostRecentPractice === null) return false;

    // A user is considered to have passed the practice if their most recent
    // practice has a passed status of true
    const activeCert = user?.mostRecentPractice?.passed === true;
    return activeCert;
  });
};

/**
 * Helper function to filter the list of managed users to those who are either failing
 * or have not attempted the certification, indicating that they may need coaching or support.
 */
export const filterUsersNeedingAttention = (
  users: IManagedUserRecentSummary[] = [],
): IManagedUserRecentSummary[] => {
  return users.filter((user) => {
    const activeCert = user.mostRecentCertificate;
    const passed = activeCert?.passed === true;
    const notAttempted = activeCert?.attemptCount === 0 || activeCert?.attemptCount === null;
    const needsAttention = !passed || notAttempted;

    return needsAttention;
  });
};

/**
 * Filters the list of managed users based on the selected filter option.
 * If no filter is selected, returns the original list.
 */
export const filterManagedUsers = (
  users: IManagedUserRecentSummary[] = [],
  filter: TManagerDashboardFilter,
) => {
  switch (filter) {
    case 'certified':
      return filterCertifiedUsers(users);
    case 'practicePassed':
      return filterPracticePassedUsers(users);
    case 'needsAttention':
      return filterUsersNeedingAttention(users);
    default:
      return users;
  }
};
